import {
  FUEL_TANK_CAP,
  GENERATOR_MODULE_ID,
  GYM_MODULE_ID,
  generatorBurnPerSec,
  getCraft,
  getHideoutModule,
  HIDEOUT_MODULES,
  isTrainableSkill,
  productionFor,
  TRAINABLE_SKILLS,
  trainingFor,
} from "./data.js";

/**
 * The hideout at rest, and the one function that moves it. There is no tick
 * loop: progression is LAZY — the state records `lastAdvanceMs` and
 * `advanceHideout` folds elapsed wall clock in as one pure step. The server
 * calls it on every touch, the client once a second for live countdowns; both
 * run the same function, so a client asleep for eight hours catches up in one
 * call. Every entry point takes `nowMs` from the SERVER's clock. Pure: fresh
 * top-level objects, unchanged sub-objects shared.
 */

// ---------------------------------------------------------------------------
// The state
// ---------------------------------------------------------------------------

/** One module's progress. `buildEndsAtMs` is present ONLY while it is building. */
export interface HideoutModuleState {
  level: number;
  buildEndsAtMs?: number;
}

/**
 * A recipe in progress. Progress is SECONDS ACCRUED, not a finish instant — a
 * powered craft does not accrue while the generator is off. A finished craft
 * sits done until collected.
 */
export interface ActiveCraft {
  craftId: string;
  progressSec: number;
}

/** A passive producer's buffer: whole units waiting, plus the part-made one. */
export interface ProductionState {
  stored: number;
  progressSec: number;
}

/** THE hideout, whole. Rides on the profile and crosses the wire verbatim. */
export interface HideoutState {
  /** Absent module id = level 0, nothing building. */
  modules: Record<string, HideoutModuleState>;
  fuelUnits: number;
  generatorOn: boolean;
  crafts: ActiveCraft[];
  /** Keyed by producing module id (today only the beehive). */
  production: Record<string, ProductionState>;
  /**
   * When each skill was last trained: skill id → server epoch ms. A last-used
   * stamp (nothing here ticks); `trainProblem` compares it against `nowMs`.
   * Per skill, so one visit can train both. Absent = never = ready.
   */
  training: Record<string, number>;
  /** Server clock (ms epoch) this state was last advanced to. */
  lastAdvanceMs: number;
}

/** A fresh cellar: nothing built, nothing running, an empty tank. */
export function createHideoutState(nowMs: number): HideoutState {
  return {
    modules: {},
    fuelUnits: 0,
    generatorOn: false,
    crafts: [],
    production: {},
    training: {},
    lastAdvanceMs: Number.isFinite(nowMs) ? nowMs : 0,
  };
}

/**
 * What level `moduleId` is BUILT to (a build in progress does not count). 0 for
 * unbuilt, unknown, or garbage.
 */
export function moduleLevel(state: HideoutState, moduleId: string): number {
  const m = ownProp(state.modules, moduleId);
  if (m === null) return 0;
  const level = m.level;
  if (!Number.isFinite(level) || level <= 0) return 0;
  return Math.floor(level);
}

/** True when the generator is switched on, built, and has fuel left — the one
 *  condition a `needsPower` recipe accrues under. */
export function isGeneratorRunning(state: HideoutState): boolean {
  if (!state.generatorOn) return false;
  if (!(state.fuelUnits > 0)) return false;
  return generatorBurnPerSec(moduleLevel(state, GENERATOR_MODULE_ID)) > 0;
}

/**
 * Reads one own property off a wire-supplied record —
 * `state.modules["toString"]` must be nothing.
 */
function ownProp<T>(rec: Record<string, T>, key: string): T | null {
  if (!Object.prototype.hasOwnProperty.call(rec, key)) return null;
  return rec[key] ?? null;
}

/**
 * Writes one own property; a plain assignment with key `"__proto__"` would run
 * the inherited setter.
 */
function setOwn<T>(rec: Record<string, T>, key: string, value: T): void {
  Object.defineProperty(rec, key, { value, writable: true, enumerable: true, configurable: true });
}

// ---------------------------------------------------------------------------
// The clock
// ---------------------------------------------------------------------------

/**
 * Folds every second between `state.lastAdvanceMs` and `nowMs` into the
 * state. Pure. Order matters and both sides must agree:
 *  1. BUILDS due by `nowMs` complete — first, so a generator upgrade finishing
 *     in the window burns at its new rate;
 *  2. FUEL burns at the current level's rate while on and not empty;
 *     `poweredSec` is what the tank could cover, and an emptied tank switches
 *     the generator OFF;
 *  3. CRAFTS gain `poweredSec` if powered, the whole window if not, capped;
 *  4. PRODUCTION converts whole periods into units and carries the remainder,
 *     except at cap, where progress freezes at 0.
 * Negative elapsed is a no-op, mark included — a hideout never goes backwards.
 */
export function advanceHideout(state: HideoutState, nowMs: number): HideoutState {
  const elapsedMs = Number.isFinite(nowMs) ? nowMs - state.lastAdvanceMs : 0;
  if (!(elapsedMs > 0)) {
    return {
      modules: state.modules,
      fuelUnits: state.fuelUnits,
      generatorOn: state.generatorOn,
      crafts: state.crafts,
      production: state.production,
      training: state.training,
      lastAdvanceMs: state.lastAdvanceMs,
    };
  }
  const elapsedSec = elapsedMs / 1000;

  // --- 1. builds ----------------------------------------------------------
  const modules: Record<string, HideoutModuleState> = {};
  for (const id of Object.keys(state.modules)) {
    const m = ownProp(state.modules, id);
    if (m === null) continue;
    const ends = m.buildEndsAtMs;
    if (ends === undefined || !Number.isFinite(ends) || ends > nowMs) {
      setOwn(modules, id, m);
      continue;
    }
    const def = getHideoutModule(id);
    const max = def === null ? m.level : def.levels.length;
    const level = m.level < max ? m.level + 1 : m.level;
    // The timer is dropped either way: a build that has run out cannot stay in
    // progress, whatever the level turned out to be.
    setOwn(modules, id, { level });
  }

  // --- 2. fuel ------------------------------------------------------------
  const burnPerSec = generatorBurnPerSec(levelIn(modules, GENERATOR_MODULE_ID));
  let fuelUnits = state.fuelUnits;
  let generatorOn = state.generatorOn;
  let poweredSec = 0;
  if (generatorOn && fuelUnits > 0 && burnPerSec > 0) {
    const tankSec = fuelUnits / burnPerSec;
    if (elapsedSec >= tankSec) {
      poweredSec = tankSec;
      fuelUnits = 0;
      generatorOn = false;
    } else {
      poweredSec = elapsedSec;
      fuelUnits -= poweredSec * burnPerSec;
    }
  }

  // --- 3. crafts ----------------------------------------------------------
  const crafts: ActiveCraft[] = state.crafts.map((c) => {
    const def = getCraft(c.craftId);
    // An unknown recipe is left exactly as it is rather than accruing against a
    // duration nobody can name; `sanitizeHideout` is what drops it.
    if (def === null) return c;
    const gained = def.needsPower ? poweredSec : elapsedSec;
    if (gained <= 0) return c;
    const progressSec = Math.min(def.craftSec, c.progressSec + gained);
    return progressSec === c.progressSec ? c : { craftId: c.craftId, progressSec };
  });

  // --- 4. production ------------------------------------------------------
  const production: Record<string, ProductionState> = { ...state.production };
  for (const def of HIDEOUT_MODULES) {
    const prod = productionFor(def.id, levelIn(modules, def.id));
    if (prod === null) continue;

    const prev = ownProp(state.production, def.id);
    const storedBefore = prev === null || !Number.isFinite(prev.stored) ? 0 : prev.stored;
    const progressBefore = prev === null || !Number.isFinite(prev.progressSec) ? 0 : prev.progressSec;

    if (storedBefore >= prod.cap) {
      setOwn(production, def.id, { stored: prod.cap, progressSec: 0 });
      continue;
    }

    let progressSec = progressBefore + elapsedSec;
    const whole = Math.floor(progressSec / prod.periodSec);
    const room = prod.cap - storedBefore;
    const made = whole > room ? room : whole;
    const stored = storedBefore + made;
    progressSec -= made * prod.periodSec;
    // At cap the part-made jar is thrown away rather than banked — that is what
    // makes the cap bite.
    setOwn(production, def.id, { stored, progressSec: stored >= prod.cap ? 0 : progressSec });
  }

  // `training` passes through by reference: a stamp in the past expires by
  // comparison, never by being advanced.
  return {
    modules,
    fuelUnits,
    generatorOn,
    crafts,
    production,
    training: state.training,
    lastAdvanceMs: nowMs,
  };
}

/** `moduleLevel`, but against a bare module record — used inside `advanceHideout`
 *  where the post-completion record is not yet a `HideoutState`. */
function levelIn(modules: Record<string, HideoutModuleState>, moduleId: string): number {
  const m = ownProp(modules, moduleId);
  if (m === null || !Number.isFinite(m.level) || m.level <= 0) return 0;
  return Math.floor(m.level);
}

// ---------------------------------------------------------------------------
// Decisions: one truth, two callers. The server refuses with the reason these
// return and the client greys the button with the same one. The two that
// spend take the stash as a `ReadonlyMap<defId, count>` (`countItemsByDef`);
// `trainProblem` takes no stash — a session is paid in patience.
// ---------------------------------------------------------------------------

/**
 * Why the next level of `moduleId` cannot start, or `null`. Cheapest first,
 * first match wins: unknown, maxed, busy, prereq (a required module not BUILT
 * that far — a build in progress does not count), money, items.
 */
export function buildProblem(
  state: HideoutState,
  euros: number,
  stashCounts: ReadonlyMap<string, number>,
  moduleId: string,
): string | null {
  const def = getHideoutModule(moduleId);
  if (def === null) return "unknown";

  const level = moduleLevel(state, moduleId);
  const next = def.levels[level];
  if (next === undefined) return "maxed";

  const st = ownProp(state.modules, moduleId);
  if (st !== null && st.buildEndsAtMs !== undefined) return "busy";

  for (const req of next.requires) {
    if (moduleLevel(state, req.moduleId) < req.level) return "prereq";
  }
  if (!(euros >= next.euros)) return "money";
  for (const cost of next.items) {
    if ((stashCounts.get(cost.defId) ?? 0) < cost.count) return "items";
  }
  return null;
}

/**
 * Why `craftId` cannot start, or `null`: unknown, level (module not built that
 * far), busy (one bench, one job), power (checked at START too, so a powered
 * craft cannot be queued against a dry tank), items.
 */
export function craftProblem(
  state: HideoutState,
  stashCounts: ReadonlyMap<string, number>,
  craftId: string,
): string | null {
  const def = getCraft(craftId);
  if (def === null) return "unknown";

  if (moduleLevel(state, def.moduleId) < def.minLevel) return "level";

  for (const active of state.crafts) {
    const running = getCraft(active.craftId);
    if (running !== null && running.moduleId === def.moduleId) return "busy";
  }

  if (def.needsPower && !isGeneratorRunning(state)) return "power";

  for (const input of def.inputs) {
    if ((stashCounts.get(input.defId) ?? 0) < input.count) return "items";
  }
  return null;
}

/**
 * Why a gym session on `skill` cannot start, or `null`: unknown (checked first
 * — it is about the request, and guards the prototype), level (no gym built),
 * cooldown. Ready the instant the last session is a full `cooldownSec` old; a
 * stamp in the FUTURE reads as still cooling — the safe direction.
 */
export function trainProblem(state: HideoutState, skill: string, nowMs: number): string | null {
  if (!isTrainableSkill(skill)) return "unknown";

  const def = trainingFor(moduleLevel(state, GYM_MODULE_ID));
  if (def === null) return "level";

  // A missing, non-numeric or non-finite stamp is "never trained", which is
  // always ready — the blob may be older than this field.
  const last = ownProp(state.training, skill);
  const at = typeof last === "number" && Number.isFinite(last) ? last : Number.NEGATIVE_INFINITY;
  const readyAfter = (Number.isFinite(nowMs) ? nowMs : 0) - def.cooldownSec * 1000;
  return at > readyAfter ? "cooldown" : null;
}

/**
 * Seconds until `skill` can be trained again — 0 the moment it can, and 0 for
 * every non-waiting reason (`trainProblem` tells those apart).
 */
export function trainingCooldownLeftSec(
  state: HideoutState,
  skill: string,
  nowMs: number,
): number {
  if (!isTrainableSkill(skill)) return 0;
  const def = trainingFor(moduleLevel(state, GYM_MODULE_ID));
  if (def === null) return 0;

  const last = ownProp(state.training, skill);
  if (typeof last !== "number" || !Number.isFinite(last)) return 0;
  const leftMs = last + def.cooldownSec * 1000 - (Number.isFinite(nowMs) ? nowMs : 0);
  return leftMs > 0 ? leftMs / 1000 : 0;
}

// ---------------------------------------------------------------------------
// Hardening
// ---------------------------------------------------------------------------

/**
 * Turns anything into a usable `HideoutState`. Never throws: a non-object is a
 * fresh hideout; unknown module/production keys are dropped; levels clamped
 * to [0, maxLevel] and a build timer on a maxed module discarded; non-finite
 * numbers become 0; crafts naming unknown recipes (or a second on one module)
 * dropped; fuel clamped; training stamps kept only for trainable skills and
 * finite epoch instants. Does NOT advance — the caller's `advanceHideout` decides.
 */
export function sanitizeHideout(v: unknown, nowMs: number): HideoutState {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return createHideoutState(nowMs);
  const raw = v as Record<string, unknown>;

  const out = createHideoutState(nowMs);

  // --- modules ------------------------------------------------------------
  const rawModules = raw["modules"];
  if (isRecord(rawModules)) {
    for (const def of HIDEOUT_MODULES) {
      const m = ownUnknown(rawModules, def.id);
      if (!isRecord(m)) continue;
      const level = clampInt(m["level"], 0, def.levels.length);
      const entry: HideoutModuleState = { level };
      const ends = m["buildEndsAtMs"];
      if (typeof ends === "number" && Number.isFinite(ends) && level < def.levels.length) {
        entry.buildEndsAtMs = ends;
      }
      setOwn(out.modules, def.id, entry);
    }
  }

  // --- generator ----------------------------------------------------------
  out.fuelUnits = clampNumber(raw["fuelUnits"], 0, FUEL_TANK_CAP);
  out.generatorOn = raw["generatorOn"] === true;

  // --- crafts -------------------------------------------------------------
  const rawCrafts = raw["crafts"];
  if (Array.isArray(rawCrafts)) {
    const busyModules = new Set<string>();
    for (const c of rawCrafts) {
      if (!isRecord(c)) continue;
      const craftId = c["craftId"];
      if (typeof craftId !== "string") continue;
      const def = getCraft(craftId);
      if (def === null) continue;
      if (busyModules.has(def.moduleId)) continue;
      busyModules.add(def.moduleId);
      out.crafts.push({ craftId: def.id, progressSec: clampNumber(c["progressSec"], 0, def.craftSec) });
    }
  }

  // --- production ---------------------------------------------------------
  const rawProduction = raw["production"];
  if (isRecord(rawProduction)) {
    for (const def of HIDEOUT_MODULES) {
      const p = ownUnknown(rawProduction, def.id);
      if (!isRecord(p)) continue;
      // Clamped against the level this state actually has, so a buffer bigger
      // than the current hive can hold is trimmed rather than trusted.
      const prod = productionFor(def.id, out.modules[def.id]?.level ?? 0);
      if (prod === null) continue;
      setOwn(out.production, def.id, {
        stored: clampInt(p["stored"], 0, prod.cap),
        progressSec: clampNumber(p["progressSec"], 0, prod.periodSec),
      });
    }
  }

  // --- training: walked over the trainable skills, not the blob's keys, so a
  // stale or prototype key is never read.
  const rawTraining = raw["training"];
  if (isRecord(rawTraining)) {
    for (const skill of TRAINABLE_SKILLS) {
      const at = ownUnknown(rawTraining, skill);
      // A garbage stamp is dropped, not clamped — "never trained" is the honest
      // reading.
      if (typeof at !== "number" || !Number.isFinite(at) || at < 0) continue;
      setOwn(out.training, skill, at);
    }
  }

  // --- the mark -----------------------------------------------------------
  const last = raw["lastAdvanceMs"];
  out.lastAdvanceMs = typeof last === "number" && Number.isFinite(last) ? last : out.lastAdvanceMs;

  return out;
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

/** Own-property read off an untrusted object, so prototype keys are nothing. */
function ownUnknown(rec: Record<string, unknown>, key: string): unknown {
  return Object.prototype.hasOwnProperty.call(rec, key) ? rec[key] : undefined;
}

function clampNumber(x: unknown, lo: number, hi: number): number {
  if (typeof x !== "number" || !Number.isFinite(x)) return lo;
  if (x < lo) return lo;
  return x > hi ? hi : x;
}

function clampInt(x: unknown, lo: number, hi: number): number {
  return Math.floor(clampNumber(x, lo, hi));
}
